import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";

import { HubBlobUrlPreview } from "@/components/HubBlobUrlPreview";
import { Button } from "@/components/ui/Button";
import { FilePicker } from "@/components/ui/FilePicker";
import { useHubToast } from "@/components/ui/HubToast";
import { Input, Label } from "@/components/ui/Input";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import { createSuntronicInvoice } from "./suntronicInvoicesApi";

function describeMutationError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function invoicePdfKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function mergeManufacturerInvoicePdfs(current: File[], added: File[]): File[] {
  const byKey = new Map(current.map((file) => [invoicePdfKey(file), file] as const));
  for (const file of added) {
    byKey.set(invoicePdfKey(file), file);
  }
  return [...byKey.values()];
}

export function NewManufacturerInvoiceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (invoiceId: number) => void;
}): JSX.Element {
  const { showToast } = useHubToast();
  const [manufacturer, setManufacturer] = useState("Suntronic");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [totalCost, setTotalCost] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [invoicePdfs, setInvoicePdfs] = useState<File[]>([]);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [pdfPickerKey, setPdfPickerKey] = useState(0);
  const [createError, setCreateError] = useState<string | null>(null);

  const previewFile =
    invoicePdfs.find((file) => invoicePdfKey(file) === previewKey) ?? invoicePdfs[0] ?? null;
  const hasPdfs = invoicePdfs.length > 0;

  const previewUrl = useMemo(() => {
    if (previewFile == null) {
      return null;
    }
    return URL.createObjectURL(previewFile);
  }, [previewFile]);

  useEffect(() => {
    return () => {
      if (previewUrl != null) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const createInvoiceMutation = useMutation({
    mutationFn: createSuntronicInvoice,
    onSuccess: (invoice) => {
      onCreated(invoice.id);
    },
    onError: (error) => {
      const message = describeMutationError(error, "Could not create invoice.");
      setCreateError(message);
      showToast({ tone: "error", message });
    },
  });

  const form = (
    <>
      {createError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
          {createError}
        </div>
      ) : null}
      <div>
        <Label htmlFor="new-manufacturer">Manufacturer</Label>
        <select
          id="new-manufacturer"
          className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
          value={manufacturer}
          onChange={(event) => setManufacturer(event.target.value)}
        >
          <option value="Suntronic">Suntronic</option>
        </select>
      </div>
      <div>
        <Label htmlFor="new-invoice-number">Invoice number</Label>
        <Input
          id="new-invoice-number"
          value={invoiceNumber}
          onChange={(event) => setInvoiceNumber(event.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="new-total-cost">Total cost</Label>
        <Input
          id="new-total-cost"
          type="number"
          step="0.01"
          value={totalCost}
          onChange={(event) => setTotalCost(event.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="new-invoice-date">Date</Label>
        <Input
          id="new-invoice-date"
          type="date"
          value={invoiceDate}
          onChange={(event) => setInvoiceDate(event.target.value)}
        />
      </div>
      <div>
        <FilePicker
          key={pdfPickerKey}
          id="new-invoice-pdfs"
          label="Add PDFs"
          hoverHint="Drop one or more PDFs onto this row, or browse. You can add the invoice and packing slip in separate picks, then select a file below to preview it."
          emptyLabel="Drop PDFs here or browse"
          accept="application/pdf,.pdf"
          multiple
          onChange={(_file, event) => {
            const added = Array.from(event.target.files ?? []);
            if (added.length === 0) {
              return;
            }
            const invalidPdf = added.find(
              (file) => file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf"),
            );
            if (invalidPdf != null) {
              const message = "Invoice attachments must be PDF files.";
              setCreateError(message);
              showToast({ tone: "error", message });
              setPdfPickerKey((current) => current + 1);
              return;
            }
            const next = mergeManufacturerInvoicePdfs(invoicePdfs, added);
            setInvoicePdfs(next);
            setPreviewKey((preview) => {
              if (preview != null && next.some((file) => invoicePdfKey(file) === preview)) {
                return preview;
              }
              return added[0] != null ? invoicePdfKey(added[0]) : next[0] != null ? invoicePdfKey(next[0]) : null;
            });
            setPdfPickerKey((current) => current + 1);
          }}
        />
        {invoicePdfs.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {invoicePdfs.map((file) => {
              const key = invoicePdfKey(file);
              const selected = previewFile != null && invoicePdfKey(previewFile) === key;
              return (
                <li key={key} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPreviewKey(key)}
                    className={cn(
                      "min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left text-sm",
                      selected
                        ? "bg-brand-50 font-medium text-brand-800"
                        : "text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    {file.name}
                  </button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => {
                      setInvoicePdfs((current) => {
                        const next = current.filter((row) => invoicePdfKey(row) !== key);
                        setPreviewKey((preview) => {
                          if (preview !== key) {
                            return preview;
                          }
                          return next[0] != null ? invoicePdfKey(next[0]) : null;
                        });
                        return next;
                      });
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => {
            const manufacturerValue = manufacturer.trim();
            const mfrInvoiceNumber = invoiceNumber.trim();
            const dateValue = invoiceDate.trim();
            const parsedTotal = Number.parseFloat(totalCost);
            if (manufacturerValue === "" || mfrInvoiceNumber === "" || dateValue === "") {
              const message = "Manufacturer, invoice number, and date are required.";
              setCreateError(message);
              showToast({ tone: "error", message });
              return;
            }
            if (!Number.isFinite(parsedTotal)) {
              const message = "Total cost must be a number.";
              setCreateError(message);
              showToast({ tone: "error", message });
              return;
            }
            const invalidPdf = invoicePdfs.find(
              (file) => file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf"),
            );
            if (invalidPdf != null) {
              const message = "Invoice attachments must be PDF files.";
              setCreateError(message);
              showToast({ tone: "error", message });
              return;
            }
            setCreateError(null);
            createInvoiceMutation.mutate({
              manufacturer: manufacturerValue,
              mfr_invoice_number: mfrInvoiceNumber,
              total_cost: parsedTotal,
              date: dateValue,
              files: invoicePdfs,
            });
          }}
          disabled={createInvoiceMutation.isPending}
        >
          {createInvoiceMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Create
        </Button>
      </div>
    </>
  );

  return (
    <DocumentModal
      title="New manufacturer invoice"
      onClose={onClose}
      showCloseButton={false}
      overlayClassName={hasPdfs ? "px-2 py-4 sm:px-4 sm:py-6" : undefined}
      shellClassName={
        hasPdfs
          ? "flex h-[min(90vh,52rem)] max-h-[calc(100vh-2rem)] w-[min(1400px,calc(100vw-1rem))] max-w-none flex-col overflow-hidden"
          : "max-w-lg"
      }
      mainClassName={hasPdfs ? "overflow-hidden" : undefined}
    >
      {hasPdfs ? (
        <div className="flex min-h-0 flex-1 flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-8">
          <div className="min-h-0 min-w-0 space-y-4 overflow-y-auto lg:max-w-md lg:flex-shrink-0">
            {form}
          </div>
          <aside className="flex min-h-[min(70vh,36rem)] min-w-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              <div className="shrink-0 border-b border-slate-200 px-3 py-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Preview</h3>
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-2 pt-0">
                {previewUrl != null && previewFile != null ? (
                  <HubBlobUrlPreview
                    previewUrl={previewUrl}
                    filename={previewFile.name}
                    contentType={previewFile.type}
                    fillAvailableHeight
                  />
                ) : (
                  <p className="py-12 text-center text-sm text-slate-500">No preview available.</p>
                )}
              </div>
            </div>
          </aside>
        </div>
      ) : (
        <div className="space-y-4">{form}</div>
      )}
    </DocumentModal>
  );
}
